<template>
  <panel light>
    <h5 slot="heading" class="panel-title">{{pageTitle}}</h5>
    <div slot="heading" class="heading-elements">
      <div class="heading-buttons">
        <router-link :to="{name: 'collection/create', query: filter}" class="btn btn-success">Ajouter une collection</router-link>
      </div>
    </div>
    <div slot="body" class="form-inline">
      <form-group>
        <form-input-search :value="filter.search" debounce @input="setFilterSearch"></form-input-search>
      </form-group>
      <form-group>
        <form-select :value="filter.category" @input="setFilterCategory" :options="categories" placeholder="Filtrer par métier"></form-select>
      </form-group>
      <form-group>
        <button v-if="isFiltered" type="button" class="btn btn-default" @click="resetFilter()">Effacer les filtres</button>
      </form-group>
    </div>

    <div slot="content" class="table-responsive">
      <table class="table table-striped table-lg">
        <thead>
        <tr>
          <th width="135">Aperçu</th>
          <th>Nom de la collection</th>
          <th>Produits</th>
          <th>Activité</th>
          <th width="100" class="text-center">Statut</th>
          <th width="80" class="text-center">Actions</th>
        </tr>
        </thead>
        <tbody v-if="collections.length">
        <tr v-for="collection in collections" :key="collection.id">
          <td class="centered">
            <media-image :media="collection.preview" empty-label="Pas d'image" thumbnail />
          </td>
          <td>
            <h6 class="no-margin text-semibold">
              <router-link :to="{name: 'collection/edit', params: {id: collection.id}}">{{collection.name}}</router-link>
            </h6>
            <p v-if="!collection.categories || !collection.categories.length" class="text-muted">(aucun métier)</p>
            <p v-else>{{getCategoriesName(collection)}}</p>
          </td>
          <td class="text-nowrap">
            <router-link :to="{name: 'products', query: {collection: collection.id}}">
              {{collection.products.length}} produit(s)
            </router-link>
          </td>
          <td class="text-nowrap">
            <activity :value="collection"></activity>
          </td>
          <td class="text-center">
            <publishing-status class="cursor-pointer" :value="collection.published" @hide="setCollectionPublished(collection, false)" @show="setCollectionPublished(collection)"></publishing-status>
          </td>
          <td class="text-center">
            <ul is="table-row-actions">
              <li>
                <router-link :to="{name: 'collection/edit', params: {id: collection.id}}"><i class="icon-pencil7"></i> Modifier</router-link>
              </li>
              <li class="divider"></li>
              <li><a @click="deleteCollection(collection)"><i class="icon-bin"></i> Supprimer</a></li>
            </ul>
          </td>
        </tr>
        </tbody>
        <tbody v-else-if="$apollo.loading">
        <tr>
          <td colspan="6" class="text-center">
            <h4>Chargement&hellip;</h4>
          </td>
        </tr>
        </tbody>
        <tbody v-else>
        <tr>
          <td colspan="6" class="text-center">
            <h4>Aucun résultat</h4>
            <router-link :to="{name: 'collection/create', query: filter}" class="btn btn-success">Ajouter une collection</router-link>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <pagination slot="footer" :total="results.totalCount" v-model="pagination"></pagination>
  </panel>
</template>

<script>
import { mapActions } from 'vuex';

import _map from "lodash/map";
import _some from 'lodash/some';

import FormInputSearch from "package-common-vuejs/src/components/form/FormInputSearch";
import Pagination from "package-common-vuejs/src/components/Pagination";
import TableRowActions from "package-common-vuejs/src/components/table/TableRowActions";
import MediaImage from "../../components/MediaImage";
import PublishingStatus from "../../components/PublishingStatus";
import Activity from "../../components/Activity";

import CollectionListQuery from "./graphql/CollectionListQuery.gql";
import CollectionRemoveMutation from "./graphql/CollectionRemoveMutation.gql";
import CategoryListQuery from "../categories/graphql/CategoryListQuery.gql";
import CollectionUpdateMutation from "./graphql/CollectionUpdateMutation.gql";

export default {
  components: {
    MediaImage,
    FormInputSearch,
    Pagination,
    TableRowActions,
    PublishingStatus,
    Activity
  },
  data() {
    return {
      results: {
        totalCount: 0,
        edges: []
      },
      categories: [],
    };
  },
  apollo: {
    results: {
      query: CollectionListQuery,
      fetchPolicy: "network-only",
      variables() {
        return {
          pagination: {
            after: this.pagination.offset,
            limit: this.pagination.limit
          },
          filter: { ...this.filter }
        };
      }
    },
    categories: {
      query: CategoryListQuery,
      fetchPolicy: "network-only",
      variables() {
        return {
          pagination: {
            limit: 999
          }
        };
      },
      update: data =>
        _map(data.results.edges, 'node')
    }
  },
  computed: {
    collections() {
      return _map(this.results.edges, "node");
    },
    filter() {
      return this.$store.state.collections.filter;
    },
    pagination: {
      get() {
        return this.$store.state.collections.pagination;
      },
      set(value) {
        this.setPagination(value);
      }
    },
    isFiltered() {
      return _some(this.filter);
    },
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.$apollo.queries.results.refetch();
    });
  },
  methods: {
    ...mapActions('collections', [
      'setFilterSearch',
      'setFilterCategory',
      'setPagination',
      'resetFilter',
    ]),
    getCategoriesName({ categories }) {
      return categories.length
        ? categories.map(({ name }) => name).join(", ")
        : "";
    },
    async setCollectionPublished(collection, published = true) {
      try {
        await this.$apollo.mutate({
          mutation: CollectionUpdateMutation,
          variables: {
            id: collection.id,
            data: {published},
          }
        });
      } catch (err) {
        this.$store.dispatch("addNotificationError", err.message);
      }
    },
    async deleteCollection(collection) {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: CollectionRemoveMutation,
          variables: {
            id: collection.id
          }
        });
        if (data.result) {
          this.$store.dispatch(
            "addNotificationSuccess",
            `La collection a été supprimée`
          );
          this.$apollo.queries.results.refetch();
        } else {
          this.$store.dispatch(
            "addNotificationError",
            `La collection n'a pas pu être supprimée`
          );
        }
      } catch (err) {
        this.$store.dispatch("addNotificationError", err.message);
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/pages/collections/CollectionsList.vue